import { MouseEvent } from 'react'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { EllipsisVertical, Trash2Icon, Pencil } from 'lucide-react'
import ConfirmDeleteDialog from './ConfirmDeleteDialog'
import RenameDialog from './RenameDialog'
import { RowAction } from './DataTableTypes'
import { useTranslation } from '@/i18n'
import { getCurrentDirection } from '@/i18n/config'
import clsx from 'clsx'
import type { DocumentRecord } from '@/types/catalog'

interface RowActionsDropdownProps {
  actions: RowAction[]
  rowType?: string
  onDelete?: (e: MouseEvent<HTMLButtonElement>) => void
  onRename?: (newName: string) => void
  id: string
  document?: DocumentRecord
}

const RowActionsDropdown = ({
  actions,
  id,
  rowType,
  document,
  onRename,
}: RowActionsDropdownProps) => {
  const { t } = useTranslation()
  const isRTL = getCurrentDirection() === 'rtl'

  const handleActionClick = (
    e: MouseEvent<HTMLDivElement>,
    actionItem: RowAction
  ) => {
    e.stopPropagation()
    actionItem.action(e, id)
  }

  // Actions applicable to THIS row (after file-type + condition filtering).
  const visibleActions = actions.filter(({ file_type, condition }) => {
    // Don't filter if no rowType or file_type provided
    if (!rowType || !file_type) {
      // Check condition if provided
      if (condition && document) {
        return condition(document)
      }
      return true
    }
    // Check both file type and condition
    const matchesFileType = file_type.includes(rowType)
    if (condition && document) {
      return matchesFileType && condition(document)
    }
    return matchesFileType
  })

  // Nothing applies to this row (e.g. an annotator on a non-P&ID doc) — render
  // nothing rather than an empty 3-dot menu that opens to a blank popover.
  if (visibleActions.length === 0) {
    return null
  }

  return (
    <div className="w-full flex gap-2">
      <Popover modal={false}>
        <PopoverTrigger asChild>
          <div
            className="p-2 flex justify-center items-center bg-gray-100 dark:bg-dark-accent hover:bg-gray-200 dark:hover:bg-dark-border rounded-full transition-all"
            onClick={(e) => e.stopPropagation()}
          >
            <EllipsisVertical className="w-5 h-5 cursor-pointer" />
          </div>
        </PopoverTrigger>
        <PopoverContent className="w-fit rounded-lg p-1" align={isRTL ? "end" : "start"}>
          <div className="w-full flex flex-col gap-0.5">
            {visibleActions
              .map((actionItem) => {
                const { name, icon } = actionItem
                return (
                  <div
                    key={name}
                    className="hover:bg-gray-100 dark:hover:bg-dark-accent cursor-pointer py-1 rounded-md"
                  >
                    <>
                      {actionItem.isRename && onRename && document ? (
                        <RenameDialog
                          currentName={document.name}
                          onConfirmRename={onRename}
                        >
                          <div className={clsx(
                            "flex gap-1.5 items-center px-2 text-sm",
                            isRTL && "flex-row-reverse text-right"
                          )}>
                            <Pencil className="w-3.5 h-3.5" />
                            <span className={clsx("capitalize", isRTL ? "text-right" : "text-left")}>{name}</span>
                          </div>
                        </RenameDialog>
                      ) : name !== 'delete' ? (
                        <div
                          className={clsx(
                            "flex gap-1.5 items-center px-2 text-sm",
                            isRTL && "flex-row-reverse text-right"
                          )}
                          onClick={(e) => handleActionClick(e, actionItem)}
                        >
                          {icon && icon}
                          <span className={clsx("capitalize", isRTL ? "text-right" : "text-left")}>{name}</span>
                        </div>
                      ) : (
                        <ConfirmDeleteDialog
                          onConfirmDelete={(
                            e: MouseEvent<HTMLButtonElement | HTMLDivElement>
                          ) =>
                            // Execute delete action on confirm deletion
                            actionItem.action(e, id)
                          }
                          type={rowType}
                        >
                          <div className="flex gap-1.5 items-center px-2 text-sm">
                            <Trash2Icon className="w-3.5 h-3.5" />
                            <span>{t('common:row_actions.delete')}</span>
                          </div>
                        </ConfirmDeleteDialog>
                      )}
                    </>
                  </div>
                )
              })}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  )
}

export default RowActionsDropdown
